import type { Edge, Node, Viewport } from 'reactflow';
import {
  buildTriggerPreview,
  defaultTriggerSettings,
  type TriggerKind,
} from './triggerWorkflowTypes';

const DEFAULT_LOAD_VIEWPORT: Viewport = { x: 40, y: 20, zoom: 0.4 };

function normalizeTriggerNodesForLoad(nodes: Node[]): Node[] {
  return nodes.map((n) => {
    if (n.type !== 'triggerNode' || !n.data || typeof n.data !== 'object') return n;
    const d = n.data as Record<string, unknown>;
    if (d.settings != null && typeof d.settings === 'object') return n;
    const rawKind = d.triggerKind;
    const kind: TriggerKind =
      rawKind === 'manual' || rawKind === 'webhook' || rawKind === 'scheduled' || rawKind === 'event'
        ? rawKind
        : 'manual';
    const settings = defaultTriggerSettings(kind);
    const preview =
      typeof d.preview === 'string' && d.preview.length > 0
        ? d.preview
        : buildTriggerPreview(kind, settings);
    return {
      ...n,
      data: {
        ...d,
        label: typeof d.label === 'string' ? d.label : 'Trigger',
        triggerKind: kind,
        settings,
        preview,
      },
    };
  });
}

/**
 * Parses persisted `definition` JSON (React Flow snapshot). Returns loose `Node[]`
 * — cast to your flow node union and pass through `sanitizeLoadedFlow` in the editor.
 */
export function parseWorkflowDefinitionString(
  definition: string | null | undefined
): { nodes: Node[]; edges: Edge[]; viewport: Viewport } | null {
  if (!definition?.trim()) return null;
  try {
    const parsed = JSON.parse(definition) as {
      format?: string;
      viewport?: Partial<Viewport>;
      nodes?: Node[];
      edges?: Edge[];
    };
    if (parsed.format !== 'reactflow' || !Array.isArray(parsed.nodes)) return null;
    const rawEdges = Array.isArray(parsed.edges) ? parsed.edges : [];
    const v = parsed.viewport;
    const viewport: Viewport =
      v && typeof v.x === 'number' && typeof v.y === 'number' && typeof v.zoom === 'number'
        ? { x: v.x, y: v.y, zoom: v.zoom }
        : DEFAULT_LOAD_VIEWPORT;
    const nodes = normalizeTriggerNodesForLoad(parsed.nodes);
    return { nodes, edges: rawEdges, viewport };
  } catch {
    return null;
  }
}

export type WorkflowDefinitionV1 = {
  format: 'reactflow';
  formatVersion: number;
  viewport: Viewport;
  nodes: ReturnType<typeof serializeWorkflowNodes>;
  edges: ReturnType<typeof serializeWorkflowEdges>;
};

/** Persistable node snapshot (no transient React Flow UI state). */
export function serializeWorkflowNodes(nodes: Node[]): Array<{
  id: string;
  type: string | undefined;
  position: { x: number; y: number };
  data: unknown;
  width?: number;
  height?: number;
  parentNode?: string;
}> {
  return nodes.map((n) => {
    const row: {
      id: string;
      type: string | undefined;
      position: { x: number; y: number };
      data: unknown;
      width?: number;
      height?: number;
      parentNode?: string;
    } = {
      id: n.id,
      type: n.type,
      position: { x: n.position.x, y: n.position.y },
      data: n.data,
    };
    if (n.width != null) row.width = n.width;
    if (n.height != null) row.height = n.height;
    if (n.parentNode != null) row.parentNode = n.parentNode;
    return row;
  });
}

export function serializeWorkflowEdges(edges: Edge[]): Array<{
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  type?: string;
  label?: string;
  animated?: boolean;
}> {
  return edges.map((e) => {
    const row: {
      id: string;
      source: string;
      target: string;
      sourceHandle?: string | null;
      targetHandle?: string | null;
      type?: string;
      label?: string;
      animated?: boolean;
    } = {
      id: e.id,
      source: e.source,
      target: e.target,
    };
    if (e.sourceHandle != null) row.sourceHandle = e.sourceHandle;
    if (e.targetHandle != null) row.targetHandle = e.targetHandle;
    if (e.type) row.type = e.type;
    if (e.label != null) row.label = String(e.label);
    if (e.animated != null) row.animated = e.animated;
    return row;
  });
}

export function buildWorkflowDefinitionString(
  nodes: Node[],
  edges: Edge[],
  viewport: Viewport
): string {
  const def: WorkflowDefinitionV1 = {
    format: 'reactflow',
    formatVersion: 1,
    viewport: { x: viewport.x, y: viewport.y, zoom: viewport.zoom },
    nodes: serializeWorkflowNodes(nodes),
    edges: serializeWorkflowEdges(edges),
  };
  return JSON.stringify(def);
}
